import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Col, DatePicker, Flex, Icons, Row, Select, Typography } from '@jcm/design-system';
import { Input, Layout, Radio, Tooltip } from 'antd';
import { RadioChangeEvent } from 'antd/lib';
import { useGovernancaContext } from '../../../../../../../context/GovernancaContext';
import ObrigacoesService from '../../../../services/Obrigacoes/Obrigacoes.service';
import { TObrigacaoLegal } from '../../../../ts/types/ObrigacoesLegais';
import {
	CriarObrigacaoLegalFormSchema,
	CriarObrigacaoLegalZodSchema,
} from '../../../../zod/validations/CriarObrigacaoLegalZodSchema';
import estilo from './index.module.scss';
import './layout.css';
import CustomAlert from '../../../../../../../shared/components/customAlert/customAlert';
import { editandoObrigacaoLegal } from '..';
import { PeriodicidadeReplicarObrigacaoEnum } from '../../../../ts/enums/PeriodicidadeReplicarObrigacaoEnum';

const { Sider } = Layout;
const { TextArea } = Input;

enum MenuSelecionadoEnum {
	DatasFixas = 'datasFixas',
	CalculoPeriodicidade = 'calculoPeriodicidade',
}

interface ObrigacoesLegaisProps {
	open: boolean;
	onClose: () => void;
	setObrigacoes: React.Dispatch<React.SetStateAction<TObrigacaoLegal[]>>;
	editandoObrigacaoLegal?: editandoObrigacaoLegal;
}

const ObrigacoesLegais: React.FC<ObrigacoesLegaisProps> = ({
	open,
	onClose,
	setObrigacoes,
	editandoObrigacaoLegal,
}) => {
	const [menuSelecionado, setMenuSelecionado] = useState(MenuSelecionadoEnum.DatasFixas);
	const [datasTexto, setDatasTexto] = useState('');
	const [abrirDatePicker, setAbrirDatePicker] = useState(false);
	const [erroValidacao, setErroValidacao] = useState<string | null>(null);
	const [avisoObservacao, setAvisoObservacao] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [alert, setAlert] = useState<{ type: 'success' | 'error'; content: string; title?: string } | null>(null);

	const { empresaAtiva } = useGovernancaContext();
	const obrigacoesService = new ObrigacoesService();

	const handleAlterarMenuPeriodicidade = (menuSelecionado: RadioChangeEvent) => {
		setMenuSelecionado(menuSelecionado.target.value);
		setErroValidacao(null);
		setValue('datas', null);
		setDatasTexto('');
	};

	const adicionarData = (data: dayjs.Dayjs | null) => {
		if (data) {
			const novaData = data.format('DD/MM/YYYY');
			setDatasTexto((prevText) => {
				const novoTexto = prevText + (prevText ? ' - ' : '') + novaData;
				setTimeout(() => validarDatas(novoTexto), 0);
				return novoTexto;
			});
		}
		setAbrirDatePicker(false);
	};

	const handleBlurValidacaoDatas = () => {
		validarDatas();
	};

	const handleFecharModalAcoes = () => {
		setAvisoObservacao('');
		setErroValidacao('');
		setDatasTexto('');
		reset();
		onClose();
		setMenuSelecionado(MenuSelecionadoEnum.DatasFixas);
	};

	const validarDatas = (textoParaValidar?: string): boolean => {
		const textoLimpo = (textoParaValidar || datasTexto).trim().replace(/-$/, '');
		setDatasTexto(textoLimpo);

		const regexFormatoData = /^\d{2}\/\d{2}\/\d{4}$/;
		const textos = textoLimpo.split('-').map((texto) => texto.trim());

		const mensagensErro: string[] = [];
		const mensagensObservacao: string[] = [];
		const datasValidas: Date[] = [];
		const datasDuplicadas: Set<string> = new Set();

		textos.forEach((texto) => {
			if (texto === '') return;

			if (!regexFormatoData.test(texto)) {
				mensagensErro.push(`O valor '${texto}' não é uma data válida no formato DD/MM/AAAA.`);
				return;
			}

			const [dia, mes, ano] = texto.split('/').map(Number);
			if (mes < 1 || mes > 12) {
				mensagensErro.push(`O valor '${texto}' possui um mês inválido (mês: ${mes}).`);
			} else if (dia < 1 || dia > 31) {
				mensagensErro.push(`O valor '${texto}' possui um dia inválido (dia: ${dia}).`);
			} else {
				const data = new Date(ano, mes - 1, dia);
				if (data.getDate() !== dia || data.getMonth() !== mes - 1 || data.getFullYear() !== ano) {
					mensagensErro.push(
						`O valor '${texto}' não corresponde a uma data válida (Dia: ${dia}, Mês: ${mes}, Ano: ${ano}).`,
					);
				} else {
					const dataStr = data.toISOString().split('T')[0];
					if (datasDuplicadas.has(dataStr)) {
						mensagensObservacao.push(`A data '${texto}' está repetida.`);
					} else {
						datasDuplicadas.add(dataStr);
						datasValidas.push(data);

						const hoje = new Date();
						hoje.setHours(0, 0, 0, 0);
						if (data < hoje) {
							mensagensObservacao.push(`A data '${texto}' já está vencida.`);
						}
					}
				}
			}
		});

		if (mensagensErro.length > 0) {
			setErroValidacao(mensagensErro.map((msg) => `- ${msg}`).join('\n'));
		} else {
			setErroValidacao(null);
		}

		if (mensagensObservacao.length > 0) {
			setAvisoObservacao(mensagensObservacao.map((msg) => `- ${msg}`).join('\n'));
		} else {
			setAvisoObservacao(null);
		}

		return mensagensErro.length === 0;
	};

	const onSubmit = async (data: CriarObrigacaoLegalFormSchema) => {
		const resultadoValidacaoFormulario = CriarObrigacaoLegalZodSchema(menuSelecionado).safeParse(data);

		if (!resultadoValidacaoFormulario.success) {
			console.error(resultadoValidacaoFormulario.error.format());
			return;
		}

		setLoading(true);

		if (menuSelecionado === MenuSelecionadoEnum.DatasFixas) {
			data.periodicidade = PeriodicidadeReplicarObrigacaoEnum.NA;
		} else {
			data.dataVencimento = dayjs(data.dataVencimento, 'DD-MM-YYYY').format('YYYY-MM-DD');
		}

		if (!editandoObrigacaoLegal?.editando) {
			try {
				if (menuSelecionado === MenuSelecionadoEnum.DatasFixas) {
					data.dataVencimento = null;
				}

				const criarObrigacaoLegalResponse = await obrigacoesService.postObrigacaoLegal(empresaAtiva ?? '', [
					data,
				] as unknown as TObrigacaoLegal[]);
				setObrigacoes((prevObrigacoes) => [...prevObrigacoes, ...criarObrigacaoLegalResponse.data.result]);

				if (criarObrigacaoLegalResponse.isSuccess) {
					setAlert({
						type: 'success',
						content: 'Obrigação legal criada com sucesso!',
						title: 'Sucesso!',
					});
				}

				handleFecharModalAcoes();
			} catch (error) {
				if (error instanceof Error) {
					setAlert({
						type: 'error',
						content: `Erro ao criar obrigação legal: ${error.message}. Tente novamente.`,
						title: 'Erro',
					});
				} else {
					setAlert({
						type: 'error',
						content: 'Erro desconhecido ao criar a obrigação legal. Tente novamente.',
						title: 'Erro',
					});
				}
			} finally {
				setLoading(false);
			}
		} else {
			try {
				if (menuSelecionado === MenuSelecionadoEnum.DatasFixas) {
					data.dataVencimento = null;
				}

				const criarObrigacaoLegalResponse = await obrigacoesService.putObrigacaoLegal(
					empresaAtiva ?? '',
					editandoObrigacaoLegal.obrigacaoLegal.id,
					data,
				);

				setObrigacoes((prevObrigacoes) =>
					prevObrigacoes.map((obrigacao) =>
						obrigacao.id === criarObrigacaoLegalResponse.data.result.id
							? criarObrigacaoLegalResponse.data.result
							: obrigacao,
					),
				);

				if (criarObrigacaoLegalResponse.data.result) {
					setAlert({
						type: 'success',
						content: 'Obrigação legal atualizada com sucesso!',
						title: 'Sucesso!',
					});
				}

				handleFecharModalAcoes();
			} catch (error) {
				if (error instanceof Error) {
					setAlert({
						type: 'error',
						content: `Erro ao criar obrigação legal: ${error.message}. Tente novamente.`,
						title: 'Erro',
					});
				} else {
					setAlert({
						type: 'error',
						content: 'Erro desconhecido ao criar a obrigação legal. Tente novamente.',
						title: 'Erro',
					});
				}
			} finally {
				setLoading(false);
			}
		}
	};

	const {
		control,
		setValue,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm<CriarObrigacaoLegalFormSchema>({
		defaultValues: useMemo(
			() => ({
				descricao: '',
				ano: new Date().getFullYear().toString(),
			}),
			[editandoObrigacaoLegal?.obrigacaoLegal],
		),
		resolver: zodResolver(CriarObrigacaoLegalZodSchema(menuSelecionado)),
		context: { menuSelecionado },
	});

	useEffect(() => {
		if (editandoObrigacaoLegal?.editando) {
			reset({
				descricao: editandoObrigacaoLegal.obrigacaoLegal.descricao,
				datas: editandoObrigacaoLegal.obrigacaoLegal.datas,
				dataVencimento: dayjs(editandoObrigacaoLegal.obrigacaoLegal.dataVencimento).format('DD-MM-YYYY'),
				formaCumprimento: editandoObrigacaoLegal.obrigacaoLegal.formaCumprimento,
				fundamentacaoLegal: editandoObrigacaoLegal.obrigacaoLegal.fundamentacaoLegal,
				periodicidade: editandoObrigacaoLegal.obrigacaoLegal.periodicidade,
				prazoLegal: editandoObrigacaoLegal.obrigacaoLegal.prazoLegal,
				temporalidade: editandoObrigacaoLegal.obrigacaoLegal.temporalidade,
				ano: new Date().getFullYear().toString(),
			});
			setDatasTexto(editandoObrigacaoLegal.obrigacaoLegal.datas);
		} else {
			reset({
				descricao: '',
				datas: '',
				dataVencimento: '',
				formaCumprimento: '',
				fundamentacaoLegal: '',
				periodicidade: 0,
				prazoLegal: '',
				temporalidade: '',
				ano: new Date().getFullYear().toString(),
			});
			setDatasTexto('');
		}
	}, [editandoObrigacaoLegal?.obrigacaoLegal, reset]);

	useEffect(() => {
		setValue('datas', datasTexto);
	}, [datasTexto]);

	return (
		<Sider
			width={500}
			collapsible
			collapsed={!open}
			collapsedWidth={0}
			trigger={null}
			className={estilo.obrigacoesSider}
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className={estilo.siderContent}>
					<Flex className={estilo.customHeader}>
						<Typography.Title size='small'>Nova obrigação legal</Typography.Title>
						<Tooltip title='Fechar' mouseLeaveDelay={0}>
							<Button
								type='text'
								variant='default'
								onClick={(ev) => {
									ev.preventDefault();
									reset();
									setErroValidacao('');
									setDatasTexto('');
									onClose();
								}}
								icon={<Icons.Close />}
								circle
							/>
						</Tooltip>
					</Flex>

					<div className={estilo.criacaoContainer}>
						<Flex vertical gap={2}>
							<Controller
								name='descricao'
								control={control}
								render={({ field }) => (
									<Flex vertical gap={2}>
										<Flex className='campoObrigatorio'>
											<Typography.Body>Descrição</Typography.Body>
										</Flex>
										<TextArea
											allowClear
											variant='filled'
											placeholder='Descrição'
											{...field}
											className={estilo.textAreaFormulario}
										/>
									</Flex>
								)}
							/>
							{errors.descricao && (
								<Typography.Label variant='error'>{errors.descricao.message as string}</Typography.Label>
							)}
						</Flex>

						<Controller
							name='fundamentacaoLegal'
							control={control}
							render={({ field }) => (
								<Flex vertical gap={2}>
									<Typography.Body className={estilo.espacamentoTitulosForm}>Fundamentação legal</Typography.Body>
									<TextArea
										allowClear
										variant='filled'
										placeholder='Fundamentação legal'
										{...field}
										className={estilo.textAreaFormulario}
									/>
								</Flex>
							)}
						/>

						<Controller
							name='prazoLegal'
							control={control}
							render={({ field }) => (
								<Flex vertical gap={2}>
									<Typography.Body className={estilo.espacamentoTitulosForm}>Prazo legal</Typography.Body>
									<TextArea
										allowClear
										variant='filled'
										placeholder='Prazo legal'
										{...field}
										className={estilo.textAreaFormulario}
									/>
								</Flex>
							)}
						/>

						<Controller
							name='temporalidade'
							control={control}
							render={({ field }) => (
								<Flex vertical gap={2}>
									<Typography.Body className={estilo.espacamentoTitulosForm}>Temporalidade</Typography.Body>
									<TextArea
										allowClear
										variant='filled'
										placeholder='Temporalidade'
										{...field}
										className={estilo.textAreaFormulario}
									/>
								</Flex>
							)}
						/>

						<Controller
							name='formaCumprimento'
							control={control}
							render={({ field }) => (
								<Flex vertical gap={2}>
									<Typography.Body className={estilo.espacamentoTitulosForm}>Forma de cumprimento</Typography.Body>
									<TextArea
										allowClear
										variant='filled'
										placeholder='Forma de cumprimento'
										{...field}
										className={estilo.textAreaFormulario}
									/>
								</Flex>
							)}
						/>
					</div>

					<div className={estilo.temporalidadeContainer}>
						<Radio.Group
							onChange={handleAlterarMenuPeriodicidade}
							value={menuSelecionado}
							className={estilo.marginBottonMedia}
						>
							<Radio value={MenuSelecionadoEnum.DatasFixas}>Datas Fixas</Radio>
							<Radio value={MenuSelecionadoEnum.CalculoPeriodicidade}>Cálculo de Periodicidade</Radio>
						</Radio.Group>

						{menuSelecionado === MenuSelecionadoEnum.DatasFixas && (
							<div className={estilo.menuContent}>
								<div className={estilo.tituloDataFixa}>
									<Flex align='center' gap={4} className={`${estilo.campoTitulo} campoObrigatorio`}>
										<Typography.Body>Inserir Datas </Typography.Body>
										<Tooltip
											title={
												<>
													Digite as datas no formato <strong>dd/mm/aaaa</strong>, separadas por vírgula. <br />
													Exemplo: <em>01/01/2023, 02/02/2023</em>. <br />
													Ou utilize o botão <strong>&quot;Adicionar data&quot;</strong> para selecionar datas no
													calendário.
												</>
											}
											placement='right'
										>
											<Icons.Info style={{ fontSize: '16px', cursor: 'pointer' }} />
										</Tooltip>
									</Flex>
									<Button
										type='outlined'
										onClick={() => setAbrirDatePicker(true)}
										className={estilo.adicionarDataBtn}
										htmlType='button'
									>
										Adicionar Data
									</Button>
								</div>

								{abrirDatePicker && (
									<DatePicker
										className='obrigacaoLegal'
										allowClear
										placeholder='DD/MM/YYYY'
										format='DD/MM/YYYY'
										variant='filled'
										onChange={adicionarData}
										open={abrirDatePicker}
										onOpenChange={(status) => !status && setAbrirDatePicker(false)}
									/>
								)}
								<TextArea
									value={datasTexto}
									name='datas'
									onChange={(e) => {
										setDatasTexto(e.target.value);
									}}
									onKeyUp={handleBlurValidacaoDatas}
									onBlur={handleBlurValidacaoDatas}
									placeholder='dd/mm/aaaa - dd/mm/aaaa'
									rows={4}
									className={estilo.textAreaFormulario}
								/>
								<Row className={estilo.espacamentoTitulosForm}>
									{erroValidacao && (
										<Typography.Label className={estilo.erroMensagem}>{erroValidacao}</Typography.Label>
									)}
									{avisoObservacao && (
										<Typography.Label className={estilo.avisoMensagem}>{avisoObservacao}</Typography.Label>
									)}
									{errors.datas && (
										<Typography.Label variant='error'>{errors.datas.message as string}</Typography.Label>
									)}
								</Row>
							</div>
						)}

						{menuSelecionado === MenuSelecionadoEnum.CalculoPeriodicidade && (
							<div className={estilo.menuContent}>
								<Flex className={estilo.gapMedio}>
									<Col className={estilo.tamanhoPeriodicidade}>
										<Flex className='campoObrigatorio'>
											<Typography.Body>Periodicidade</Typography.Body>
										</Flex>
										<Controller
											name='periodicidade'
											control={control}
											render={({ field }) => (
												<>
													<Select
														{...field}
														variant='outlined'
														placeholder='Selecione'
														options={[
															{
																label: 'Única',
																value: 0,
															},
															{
																label: 'Diária',
																value: 1,
															},
															{
																label: 'Semanal',
																value: 2,
															},
															{
																label: 'Quinzenal',
																value: 3,
															},
															{
																label: 'Mensal',
																value: 4,
															},
															{
																label: 'Bimestral',
																value: 5,
															},
															{
																label: 'Trimestral',
																value: 6,
															},
															{
																label: 'Quadrimestral',
																value: 7,
															},
															{
																label: 'Semestral',
																value: 8,
															},
															{
																label: 'Anual',
																value: 9,
															},
															{
																label: 'Decendial',
																value: 10,
															},
															{
																label: 'Quando Aplicável',
																value: 11,
															},
															{ label: 'N/A', value: 12, disabled: true },
														]}
													/>
												</>
											)}
										/>
										{errors.periodicidade && (
											<Typography.Label variant='error'>{errors.periodicidade.message as string}</Typography.Label>
										)}
									</Col>
									<Col>
										<Flex className='campoObrigatorio'>
											<Typography.Body>Data de Vencimento</Typography.Body>
										</Flex>
										<Controller
											name='dataVencimento'
											control={control}
											render={({ field }) => (
												<>
													<DatePicker
														placeholder='DD/MM/YYYY'
														allowClear={false}
														format='DD/MM/YYYY'
														variant='outlined'
														value={
															field.value
																? dayjs(field.value, 'DD-MM-YYYY').isValid()
																	? dayjs(field.value, 'DD-MM-YYYY')
																	: null
																: null
														}
														onChange={(e) => {
															const formattedDate = e ? dayjs(e).format('DD-MM-YYYY') : '';
															field.onChange(formattedDate);
															setValue('dataVencimento', formattedDate);
														}}
														onFocus={(e) => e.target.blur()}
													/>
													{errors.dataVencimento && (
														<Typography.Label variant='error'>
															{errors.dataVencimento.message as string}
														</Typography.Label>
													)}
												</>
											)}
										/>
									</Col>
								</Flex>
							</div>
						)}
					</div>
					<Flex className={estilo.btnContainer}>
						<Button
							className={estilo.btnCancelar}
							type='filled-tonal'
							variant='error'
							onClick={(ev) => {
								ev.preventDefault();
								reset();
								setErroValidacao('');
								setDatasTexto('');
								onClose();
								reset();
							}}
							itemType='button'
							disabled={loading}
						>
							Cancelar
						</Button>
						<Button
							variant='save'
							type='filled'
							loading={loading}
							disabled={erroValidacao !== null && erroValidacao.length > 1 ? true : false}
						>
							Salvar
						</Button>
					</Flex>
				</div>
			</form>
			{alert && <CustomAlert type={alert.type} content={alert.content} title={alert.title} />}
		</Sider>
	);
};

export default ObrigacoesLegais;
